import React, { useEffect, useState } from 'react';
import { Button, Switch } from '@roger/ui-components';

import { Check, Edit, Loader2 } from 'lucide-react';

import { useAdminSectionAccess } from '../../hooks/useAdminSectionAccess';
import { useShallow } from 'zustand/react/shallow';
import { useStore } from '../../store';
import {
  getAgencyConfig,
  updateAgencyConfig,
} from '../../services/agencyConfig';

type ChartWorkflowKey =
  | 'allow_submit_without_signature'
  | 'require_clean_oasis_for_clinician_signature'
  | 'require_clean_oasis_for_qa_signature';

type ChartWorkflowValues = Record<ChartWorkflowKey, boolean>;

const SETTINGS: {
  key: ChartWorkflowKey;
  label: string;
  description: string;
  defaultValue: boolean;
}[] = [
  {
    key: 'allow_submit_without_signature',
    label: 'Submit without signing',
    description:
      'Clinicians can submit a chart before signing it. Case managers can return one without approving. The signature is still owed.',
    defaultValue: true,
  },
  {
    key: 'require_clean_oasis_for_clinician_signature',
    label: 'Require clinicians to fix OASIS transmission errors before signing',
    description:
      'Clinicians cannot sign an OASIS chart until the All CMS scrubber shows no errors.',
    defaultValue: false,
  },
  {
    key: 'require_clean_oasis_for_qa_signature',
    label: 'Require QA to fix OASIS transmission errors before signing',
    description:
      'QA cannot sign or complete an OASIS chart until the All CMS scrubber shows no errors.',
    defaultValue: true,
  },
];

const DEFAULTS = Object.fromEntries(
  SETTINGS.map(s => [s.key, s.defaultValue])
) as ChartWorkflowValues;

/**
 * Agency policy for how charts move between clinicians and QA: whether a chart
 * can advance without a signature (ROG-3268), and whether an OASIS chart must
 * pass the All CMS scrubber before it can be signed.
 *
 * These are the agency's call, not rollout gates, which is why they sit on the
 * agency Admin Panel rather than Internal Admin and why they are stored on
 * AgencyConfig rather than behind a feature flag.
 */
const ChartWorkflowSection: React.FC = () => {
  const { canEdit } = useAdminSectionAccess('chart-workflow');
  const { agencyId, showSuccess, showError } = useStore(
    useShallow(state => ({
      agencyId: state.user?.agency?.id,
      showSuccess: state.showSuccess,
      showError: state.showError,
    }))
  );

  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  // A read that failed tells us nothing about this agency's settings, so the
  // card must not offer to write them. Without this the screen would report
  // the product defaults as if they were the agency's stored answers, and an
  // admin could save values derived from a failed read.
  const [loadFailed, setLoadFailed] = useState(false);
  const [values, setValues] = useState<ChartWorkflowValues>(DEFAULTS);
  // Last known server values. The edit state is compared against them so
  // Cancel has something true to restore, and so a no-op save writes nothing.
  const [savedValues, setSavedValues] = useState<ChartWorkflowValues>(DEFAULTS);

  useEffect(() => {
    if (!agencyId) return;
    let cancelled = false;

    const load = async () => {
      try {
        const cfg = await getAgencyConfig();
        if (cancelled) return;
        const loaded = Object.fromEntries(
          SETTINGS.map(s => [s.key, cfg[s.key] ?? s.defaultValue])
        ) as ChartWorkflowValues;
        setValues(loaded);
        setSavedValues(loaded);
      } catch (err) {
        console.error('Failed to load agency config:', err);
        if (!cancelled) setLoadFailed(true);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [agencyId]);

  const handleCancel = () => {
    setValues(savedValues);
    setIsEditing(false);
  };

  const handleSave = async () => {
    // Only the toggles that moved. The same endpoint also carries the Intake
    // Manager column layout, which this screen never edits and must not reset.
    const changed = Object.fromEntries(
      SETTINGS.filter(s => values[s.key] !== savedValues[s.key]).map(s => [
        s.key,
        values[s.key],
      ])
    ) as Partial<ChartWorkflowValues>;
    if (Object.keys(changed).length === 0) {
      setIsEditing(false);
      return;
    }

    setIsSaving(true);
    try {
      await updateAgencyConfig(changed);
      setSavedValues(values);
      setIsEditing(false);
      showSuccess('Chart workflow settings updated');
    } catch (err) {
      console.error('Failed to update agency config:', err);
      setValues(savedValues);
      showError('Failed to update chart workflow settings');
    } finally {
      setIsSaving(false);
    }
  };

  const showDisplayView = !isEditing || !canEdit;

  return (
    <div className='space-y-6'>
      <div className='space-y-1'>
        <p className='text-sm text-muted-foreground'>
          How charts move between clinicians and case managers
        </p>
        {loadFailed && (
          <p className='text-xs text-destructive'>
            These settings did not load. Reload the page to see them.
          </p>
        )}
      </div>
      <div>
        <div className='space-y-6'>
          {SETTINGS.map(setting => (
            <div
              key={setting.key}
              data-testid={`setting-${setting.key}`}
              className='flex items-center justify-between gap-4 rounded-md border p-3'
            >
              <div className='space-y-0.5'>
                <label htmlFor={setting.key} className='text-sm font-medium'>
                  {setting.label}
                </label>
                <p className='text-xs text-muted-foreground'>
                  {setting.description}
                </p>
              </div>
              {isLoading ? (
                <Loader2
                  className='h-4 w-4 shrink-0 animate-spin text-muted-foreground'
                  aria-label='Loading setting'
                />
              ) : loadFailed ? (
                <span className='shrink-0 whitespace-nowrap text-sm font-medium text-muted-foreground'>
                  Unavailable
                </span>
              ) : !showDisplayView ? (
                <Switch
                  id={setting.key}
                  checked={values[setting.key]}
                  onCheckedChange={checked =>
                    setValues(prev => ({ ...prev, [setting.key]: checked }))
                  }
                />
              ) : (
                <span
                  className={`shrink-0 whitespace-nowrap text-sm font-medium ${
                    values[setting.key]
                      ? 'text-green-600'
                      : 'text-muted-foreground'
                  }`}
                >
                  {values[setting.key] ? 'Enabled' : 'Disabled'}
                </span>
              )}
            </div>
          ))}

          <div className='flex justify-end gap-2'>
            {isEditing && canEdit ? (
              <>
                <Button
                  variant='outline'
                  onClick={handleCancel}
                  disabled={isSaving}
                >
                  Cancel
                </Button>
                <Button onClick={handleSave} disabled={isSaving}>
                  {isSaving ? (
                    <>
                      <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Check className='mr-2 h-4 w-4' />
                      Save Changes
                    </>
                  )}
                </Button>
              </>
            ) : (
              canEdit && (
                <Button
                  variant='outline'
                  onClick={() => setIsEditing(true)}
                  disabled={isLoading || loadFailed}
                >
                  <Edit className='mr-2 h-4 w-4' />
                  Edit
                </Button>
              )
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChartWorkflowSection;
